// Interface para os planos de extensão
export interface ExtensionPlan {
  id: string;
  name: string;
  description: string;
  features: string[];
  auto_recurring: {
    frequency: number;
    frequency_type: string;
    transaction_amount: number;
    currency_id: string;
  };
}

// Interface para as extensões disponíveis
export interface Extension {
  id: string;
  title: string;
  description: string;
  status: string;
  plan: ExtensionPlan;
  disabled?: boolean;
  priceInfo?: string;
}

/**
 * Extensões de Gestão
 *
 * Lista de extensões relacionadas à gestão financeira e de agenda da clínica.
 * Inclui FluxPay, Gestão de Pós-Atendimento e Google Agenda.
 */
export const gestaoExtensions: Extension[] = [
  {
    id: "marketplace",
    title: "FluxPay",
    description:
      "FluxPay é uma extensão do Fluxmed para pagamentos de consultas pelo paciente para sua clínica, que automatiza e simplifica o ciclo de pagamentos da jornada do atendimento.",
    status: "Disponível", // Será atualizado dinamicamente baseado no contrato
    priceInfo: "R$ 3,50/consulta",
    plan: {
      id: "marketplace_plan_001",
      name: "Plano Marketplace Fluxmed",
      description: "Integração com Mercado Pago para recebimento de pagamentos",
      features: [
        "Receber pagamentos online de seus pacientes",
        "Gerenciar recebimentos em uma única plataforma",
        "Acompanhar histórico e status de vendas",
        "Emitir reembolsos quando necessário",
      ],
      auto_recurring: {
        frequency: 1,
        frequency_type: "months",
        transaction_amount: 99.9,
        currency_id: "BRL",
      },
    },
  },
  {
    id: "post_attendance",
    title: "FluxZap",
    description:
      "Integre o número de WhatsApp da sua clínica e faça o acompanhamento da jornada clínica de seu paciente de forma integrada. Inclui 20 mensagens gratuitas com o número de WhatsApp padrão do Fluxmed (até a ativação do seu número de WhatsApp).",
    status: "Disponível", // Será atualizado dinamicamente baseado no contrato
    priceInfo: "R$ 50,00/mês",
    plan: {
      id: "post_attendance_plan_001",
      name: "Plano Gestão de Pós Atendimento",
      description: "Gestão completa do pós atendimento dos pacientes",
      features: [
        "Acompanhamento do progresso do tratamento",
        "Lembretes automáticos de consultas",
        "Histórico de interações com pacientes",
        "Gestão de feedback e satisfação",
      ],
      auto_recurring: {
        frequency: 1,
        frequency_type: "months",
        transaction_amount: 0,
        currency_id: "BRL",
      },
    },
  },
  {
    id: "google_calendar",
    title: "Google Agenda",
    description:
      "Integre sua agenda do Google Agenda com o Fluxmed para sincronização automática de consultas. Com esta extensão, você pode gerenciar seus compromissos de forma eficiente, evitando conflitos de horários e mantendo suas agendas sempre atualizadas em ambas as plataformas. A sincronização bidirecional garante que todas as alterações sejam refletidas instantaneamente, otimizando seu fluxo de trabalho.",
    status: "Em desenvolvimento",
    disabled: true,
    priceInfo: "Gratuito",
    plan: {
      id: "google_calendar_plan_001",
      name: "Integração Google Agenda",
      description: "Sincronização automática com Google Agenda",
      features: [
        "Sincronização automática de consultas",
        "Atualização em tempo real da agenda",
        "Prevenção de conflitos de horários",
        "Gerenciamento centralizado de compromissos",
        "Integração bidirecional com Google Agenda",
      ],
      auto_recurring: {
        frequency: 1,
        frequency_type: "months",
        transaction_amount: 0,
        currency_id: "BRL",
      },
    },
  },
  {
    id: "health_plan",
    title: "Emissão de Guias TISS",
    description:
      "Realiza atendimentos por Plano de saúde! Emissão da guias eletrônicas XML TISS - Troca de Informações na Saúde Suplementar. Essa extensão permite que guias eletrônicas TISS, em conformidade com os padrões definidos pela ANS, sejam emitidas sempre que um atendimento for realizado por plano de saúde, de forma fácil e segura. Ative, configure e usufrua de mais essa facilidade do Fluxmed pra a gestão da sua clínica ou consultório.",
    status: "Disponível",
    // priceInfo: "Gratuito",
    plan: {
      id: "health_plan_plan_001",
      name: "Plano de Saúde",
      description: "Gestão completa de planos de saúde dos pacientes",
      features: [
        "Cadastro e consulta de planos de saúde",
        "Gestão de autorizações e cobranças",
        "Histórico completo de planos por paciente",
        "Integração com sistemas de planos de saúde",
        "Relatórios e análises de cobertura",
      ],
      auto_recurring: {
        frequency: 1,
        frequency_type: "months",
        transaction_amount: 0,
        currency_id: "BRL",
      },
    },
  },
  {
    id: "pronto_atendimento",
    title: "Pronto Atendimento",
    description:
      "Atenda pacientes na hora, sem horário pré-agendado. Ative a disponibilidade, configure modo e valor, e inicie atendimentos avulsos com fila integrada à agenda. Extensão em beta — ative e experimente na área de agenda do profissional.",
    status: "Em desenvolvimento",
    priceInfo: "Gratuito",
    plan: {
      id: "pronto_atendimento_plan_001",
      name: "Pronto Atendimento",
      description: "Atendimento avulso com fila e disponibilidade imediata",
      features: [
        "Disponibilidade para atender agora",
        "Atendimento avulso sem slot prévio",
        "Fila de pacientes na agenda",
        "Configuração de modo, valor e local",
      ],
      auto_recurring: {
        frequency: 1,
        frequency_type: "months",
        transaction_amount: 0,
        currency_id: "BRL",
      },
    },
  },
  {
    id: "teleconsultoria",
    title: "Teleconsultoria",
    description:
      "Receba convites de videochamada de outros profissionais e entre na sala a partir do prontuário do paciente. Extensão gratuita.",
    status: "Disponível",
    priceInfo: "Gratuito",
    plan: {
      id: "teleconsultoria_plan_001",
      name: "Teleconsultoria",
      description: "Convites de videochamada com abertura no prontuário",
      features: [
        "Lista de convites recebidos",
        "Nome do paciente no convite",
        "Entrada na videochamada pelo prontuário",
      ],
      auto_recurring: {
        frequency: 1,
        frequency_type: "months",
        transaction_amount: 0,
        currency_id: "BRL",
      },
    },
  },
  {
    id: "questionnaires",
    title: "Questionários de Saúde",
    description:
      "Realize questionários de saúde para seus atendimentos! Essa extensão permite que questionários de saúde sejam aplicados aos pacientes durante ou antes dos atendimentos, de forma fácil e segura. Crie, gerencie e analise questionários personalizados para coletar informações importantes sobre a saúde dos seus pacientes, facilitando o diagnóstico e o acompanhamento. Ative, configure e usufrua de mais essa facilidade do Fluxmed para a gestão da sua clínica ou consultório.",
    status: "Disponível",
    plan: {
      id: "questionnaires_plan_001",
      name: "Plano Questionários de Saúde",
      description:
        "Gestão completa de questionários de saúde para atendimentos",
      features: [
        "Criação e personalização de questionários de saúde",
        "Aplicação de questionários durante ou antes dos atendimentos",
        "Histórico completo de respostas por paciente",
        "Análise e relatórios de questionários",
        "Integração com prontuário eletrônico",
        "Questionários padronizados e personalizados",
      ],
      auto_recurring: {
        frequency: 1,
        frequency_type: "months",
        transaction_amount: 0,
        currency_id: "BRL",
      },
    },
  },
  // {
  //   id: "crm",
  //   title: "FluxZap",
  //   description:
  //     "Mantenha contato com seus pacientes via WhatsApp. O FluxZap é uma extensão que permite o envio de mensagens WhatsApp para o regresso de seus pacientes à rotina de cuidado com sua clínica. Encontre pacientes atendidos previamente e retome o contato de forma simples e descomplicada.",
  //   status: "Disponível",
  //   // priceInfo: "Pacotes de mensagens",
  //   plan: {
  //     id: "crm_plan_001",
  //     name: "Plano CRM Fluxmed",
  //     description: "Gestão completa do relacionamento com pacientes",
  //     features: [
  //       "Envio de mensagens automáticas via WhatsApp e Email",
  //       "Histórico completo de interações com pacientes",
  //       "Campanhas de marketing personalizadas",
  //       "Lembretes automáticos de consultas e retornos",
  //       "Pesquisas de satisfação automatizadas",
  //       "Segmentação de pacientes por perfil",
  //       "Relatórios e análises de engajamento",
  //     ],
  //     auto_recurring: {
  //       frequency: 1,
  //       frequency_type: "months",
  //       transaction_amount: 99.9,
  //       currency_id: "BRL",
  //     },
  //   },
  // },
  {
    id: "fluxsigner",
    title: "FluxSigner - Assinatura Digital",
    description:
      "Assine documentos médicos com certificado digital ICP-Brasil diretamente do seu navegador. O FluxSigner permite a assinatura de receitas, atestados e outros documentos de forma rápida, segura e com validade jurídica. Compatível com certificados A1 e A3, token USB e smart cards.",
    status: "Disponível",
    priceInfo: "Gratuito",
    plan: {
      id: "fluxsigner_plan_001",
      name: "Plano FluxSigner",
      description: "Assinatura digital com certificados ICP-Brasil",
      features: [
        "Assinatura digital com certificados ICP-Brasil",
        "Suporte a certificados A1 e A3",
        "Assinatura rápida direto do navegador",
        "Compatível com token USB e smart cards",
        "Sem necessidade de upload de documentos",
        "100% seguro e com validade jurídica",
      ],
      auto_recurring: {
        frequency: 1,
        frequency_type: "months",
        transaction_amount: 0,
        currency_id: "BRL",
      },
    },
  },
];
